import { IonModal } from "@ionic/react";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { sendEmail } from "../../services/email.service";

interface Props {
  isOpen: boolean;
  onDismiss(): void;
  user?: any;
  study?: any;
}

const ReportBugModal: FC<Props> = ({ isOpen, onDismiss, user, study }) => {
  const { t } = useTranslation();

  const [email, setEmail] = useState(user?.attributes?.email || "");
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const categories = [
    { value: "Signaler un bug", label: t("mentalplus_menu_signaler_un_bug") },
    { value: "Contacter l'équipe", label: t("mentalplus_menu_contacter_l_equipe") },
    { value: "Autre", label: t("mentalplus_menu_autre") },
  ];

  
  const resetForm = () => {
    setSubject("");
    setDescription("");
    setError(null);
    setSuccess(false);
  };


  const handleClose = () => {
    resetForm();
    onDismiss();
  };

  const handleSubmit = async () => {
    if (!email.trim() || !subject.trim() || !description.trim()) {
      setError(t("common_all_fields_required"));
      return;
    }

    setError(null);
    setLoading(true);

    try {
      const selectedSubject =
        categories.find((item) => item.value === subject)?.label || subject;

      const emailData = {
        template: "USERSEQUENCENOTIFICATION",
        from: "noreply@mentalplus.fr",
        to: ["hosseinjamshidi87@gmail.com"],
        lang: "FR",
        params: {
          url: "https://app.mentalplus.fr",
          FRONT_URL: "https://app.mentalplus.fr/mentalplus",
          body: `<pre>
Objet: ${selectedSubject}

Bug Description: ${description}
User Email: ${email}
User ID: ${user?.id ?? "N/A"}
Study ID: ${study?.id ?? "N/A"}
          </pre>`,
          title: `Bug report - ${selectedSubject}`,
        },
        studyId: study?.id,
      };

      const response = await sendEmail(emailData);
      console.log("Email sent successfully:", response.data);

      setSuccess(true);

     
      setTimeout(() => {
        handleClose();
      }, 1500);
    } catch (error) {
      console.error("Error sending bug report email:", error);
      setError(t("common_error_occurred"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <IonModal
      className="resonance-modal"
      isOpen={isOpen}
      backdropDismiss={true}
      onWillDismiss={handleClose}
    >
      <Container onClick={handleClose}>
        <Card onClick={(e) => e.stopPropagation()}>
          <Title>{t("mentalplus_menu_conatct")}</Title>

          {success ? (
            <Success>{t("common_thanks_bug_report")}</Success>
          ) : (
            <>
              <Form>
                <Label>{t("common_your_email")}</Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />

                <Label>{t("mentalplus_menu_objet")}</Label>
                <Select
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                >
                  <option value="" disabled>
                    {t("mentalplus_menu_objet_champ")}
                  </option>
                  {categories.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </Select>

                <Label>{t("common_bug_description")}</Label>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") e.stopPropagation();
                  }}
                />

                {error && <Error>{error}</Error>}
              </Form>

              <Buttons>
                <ModalButton onClick={handleClose}>
                  {t("common_cancel")}
                </ModalButton>
                <ModalButton
                  onClick={handleSubmit}
                  style={{ opacity: loading ? 0.6 : 1 }}
                >
                  {loading ? t("common_sending") + "..." : t("common_submit")}
                </ModalButton>
              </Buttons>
            </>
          )}
        </Card>
      </Container>
    </IonModal>
  );
};

// --- styles ---
const Container = styled.div`
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Card = styled.div`
  width: 100%;
  max-width: 400px;
  background-color: white;
  border-radius: 22px;
  padding: 20px;
`;

const Title = styled.div`
  font-size: 18px;
  font-weight: bold;
  text-align: center;
`;

const Success = styled.div`
  color: green;
  text-align: center;
  font-weight: bold;
  margin: 30px 0;
`;

const Form = styled.div`
  margin-top: 20px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  margin-bottom: 5px;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  margin-bottom: 15px;
  border: 1px solid #ccc;
  border-radius: 5px;
`;

const Select = styled.select`
  width: 100%;
  padding: 10px;
  margin-bottom: 15px;
  border: 1px solid #ccc;
  border-radius: 5px;
  background-color: white;
  cursor: pointer;
`;

const Textarea = styled.textarea`
  width: 100%;
  height: 150px;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 5px;
  resize: vertical;
`;

const Error = styled.div`
  color: red;
  font-size: 12px;
`;

const Buttons = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
`;

const ModalButton = styled.div`
  padding: 10px 20px;
  color: ${(props) => props.theme.primary.main};
  font-weight: bold;
  cursor: pointer;
  border: 1px solid ${(props) => props.theme.primary.main};
  border-radius: 5px;
  margin-left: 10px;
`;

export default ReportBugModal;